import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard } from '@/lib/adminApi';
import { STATUS_LABELS } from '@/lib/adminApi';

const StatCard = ({ label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="bg-white/70 border border-neutral-200/60 rounded-xl px-6 py-6"
  >
    <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">{label}</div>
    <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-4xl leading-none text-neutral-900">
      {value ?? 0}
    </div>
    {sub && <div className="mt-3 text-[12px] text-neutral-500">{sub}</div>}
  </div>
);

const TYPE_LABEL = {
  contact: 'Contact',
  speaking: 'Speaking',
  newsletter: 'Newsletter',
  waitlist: 'Waitlist',
};

const TYPE_LINK = {
  contact: '/founder-command/contacts',
  speaking: '/founder-command/speaking',
  newsletter: '/founder-command/newsletter',
  waitlist: '/founder-command/waitlist',
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const d = await getDashboard();
        setData(d);
      } catch (err) {
        setError('Unable to load dashboard.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="uppercase tracking-[0.24em] text-[11px] text-neutral-500">Loading overview…</div>;
  }
  if (error) {
    return <div className="text-red-700">{error}</div>;
  }

  const s = data.stats;
  return (
    <div data-testid="admin-dashboard">
      <header className="mb-12">
        <div className="uppercase tracking-[0.3em] text-[10px] text-neutral-500 mb-3">Overview</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-4xl lg:text-5xl leading-tight text-neutral-900">
          Founder Command Center
        </h1>
        <p className="mt-4 text-neutral-600 max-w-xl">
          Every inquiry, request, and subscriber routed to iamtoiawallace.com — in one quiet place.
        </p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard testid="stat-contacts" label="Contacts" value={s.contacts_total} sub={`${s.contacts_new} new`} />
        <StatCard testid="stat-speaking" label="Speaking" value={s.speaking_total} sub={`${s.speaking_new} new`} />
        <StatCard testid="stat-newsletter" label="Newsletter" value={s.newsletter_total} sub={`${s.newsletter_active} active`} />
        <StatCard testid="stat-waitlist" label="Waitlist" value={s.waitlist_total} sub={`${s.waitlist_new} new`} />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
        <StatCard testid="stat-today" label="Today" value={s.today} sub="new submissions" />
        <StatCard testid="stat-week" label="This Week" value={s.week} sub="new submissions" />
        <StatCard testid="stat-followup" label="Needs Follow-Up" value={s.needs_followup} sub="across all" />
        <StatCard testid="stat-sync-errors" label="Sync Errors" value={s.newsletter_sync_errors} sub="newsletter → Resend" />
      </section>

      <section className="bg-white/70 border border-neutral-200/60 rounded-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-neutral-200/60 flex items-center justify-between">
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-1">Recent Activity</div>
            <div className="text-[15px] text-neutral-900">Latest 10 submissions across all channels</div>
          </div>
        </div>
        <div className="divide-y divide-neutral-200/60" data-testid="recent-activity-list">
          {data.recent_activity.length === 0 && (
            <div className="px-6 py-10 text-center text-neutral-500 text-[13px]">No activity yet.</div>
          )}
          {data.recent_activity.map((item, idx) => (
            <Link
              key={`${item.type}-${item.id}-${idx}`}
              to={TYPE_LINK[item.type] || '/founder-command'}
              className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-neutral-50 transition-colors"
              data-testid="recent-activity-item"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="uppercase tracking-[0.2em] text-[9px] text-[#C8A15A]">
                    {TYPE_LABEL[item.type] || item.type}
                  </span>
                  {item.status && (
                    <span className="uppercase tracking-[0.16em] text-[9px] text-neutral-500">
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                  )}
                </div>
                <div className="text-[14px] text-neutral-900 truncate">
                  {item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email}
                  {item.organization ? <span className="text-neutral-500"> · {item.organization}</span> : null}
                  {item.product ? <span className="text-neutral-500"> · {item.product}</span> : null}
                </div>
              </div>
              <div className="text-[11px] text-neutral-400 shrink-0">
                {item.created_at ? new Date(item.created_at).toLocaleString() : ''}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
